import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Activity,
  Database, Mail, Bell, Link as LinkIcon, Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const ICONS = {
  app_url: LinkIcon,
  mongodb: Database,
  brevo: Mail,
  webpush: Bell,
  seed: Users,
};

const STATUS_STYLE = {
  ok:      { icon: CheckCircle2, cls: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200",  badge: "bg-emerald-100 text-emerald-800", label: "OK" },
  warning: { icon: AlertTriangle, cls: "text-amber-600",  bg: "bg-amber-50 border-amber-200",     badge: "bg-amber-100 text-amber-800",   label: "Attenzione" },
  error:   { icon: XCircle,       cls: "text-red-600",    bg: "bg-red-50 border-red-200",         badge: "bg-red-100 text-red-800",       label: "Errore" },
};

export default function Health() {
  const { user } = useAuth();
  const allowed = ["admin", "direzione"].includes(user?.role);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["health-config"],
    queryFn: async () => (await api.get("/health/config")).data,
    enabled: allowed,
    refetchOnWindowFocus: false,
  });

  if (!allowed) {
    return (
      <div className="p-10 max-w-lg mx-auto text-center" data-testid="health-access-denied">
        <Activity size={36} className="mx-auto text-stone-400 mb-3"/>
        <div className="font-medium">Accesso riservato</div>
        <div className="text-sm text-stone-500 mt-1">Solo admin/direzione può vedere lo stato di sistema.</div>
      </div>
    );
  }

  const overall = data?.overall;
  const overallStyle = overall ? STATUS_STYLE[overall] : STATUS_STYLE.ok;
  const OverallIcon = overallStyle.icon;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto">
      <Link to="/impostazioni" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
        <ArrowLeft size={13}/> Torna alle impostazioni
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <PageHeader
          title="Stato del sistema"
          subtitle="Diagnostica configurazione, integrazioni e servizi critici."
        />
        <Button
          data-testid="health-refresh"
          variant="outline"
          size="sm"
          className="rounded-full mt-4"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw size={13} className={`mr-1.5 ${isFetching ? "animate-spin" : ""}`}/> Ricontrolla
        </Button>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-stone-400 text-sm">Verifica in corso…</div>
      ) : data ? (
        <>
          {/* Riepilogo generale */}
          <Card className={`p-5 border-2 rounded-2xl shadow-none mb-6 ${overallStyle.bg}`} data-testid="health-overall">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full grid place-items-center bg-white ${overallStyle.cls}`}>
                <OverallIcon size={26} strokeWidth={2}/>
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold tracking-tight">
                  {overall === "ok" && "Tutti i sistemi funzionanti"}
                  {overall === "warning" && "Alcuni servizi in stato di attenzione"}
                  {overall === "error" && "Sono presenti errori critici"}
                </div>
                <div className="text-xs text-stone-600 mt-0.5">
                  Ultimo controllo: {new Date(data.generated_at).toLocaleString("it-IT")}
                </div>
              </div>
              <Badge className={`${overallStyle.badge} border-0 rounded-full`}>{overallStyle.label}</Badge>
            </div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div className="rounded-lg bg-white/70 border border-stone-200 p-2.5">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">APP URL</div>
                <div className="mt-0.5 font-mono text-[11px] break-all">{data.environment.app_url || "—"}</div>
              </div>
              <div className="rounded-lg bg-white/70 border border-stone-200 p-2.5">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Database</div>
                <div className="mt-0.5 font-mono text-[11px]">{data.environment.db_name}</div>
              </div>
            </div>
          </Card>

          {/* Lista check */}
          <div className="space-y-3">
            {data.checks.map((c) => {
              const style = STATUS_STYLE[c.status] || STATUS_STYLE.warning;
              const Icon = ICONS[c.key] || Activity;
              const StatusIcon = style.icon;
              return (
                <Card
                  key={c.key}
                  data-testid={`health-check-${c.key}`}
                  className="p-4 border border-stone-200 rounded-xl shadow-none flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-lg bg-stone-100 grid place-items-center text-stone-700 shrink-0">
                    <Icon size={18}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-sm">{c.label}</div>
                      <div className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${style.badge}`}>
                        <StatusIcon size={11}/>
                        <span>{style.label}</span>
                      </div>
                    </div>
                    <div className="text-xs text-stone-600 mt-1">{c.value}</div>
                    {c.hint && c.status !== "ok" && (
                      <div className="text-[11px] text-stone-500 mt-1.5 italic">💡 {c.hint}</div>
                    )}
                    {c.extra && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {Object.entries(c.extra).filter(([, v]) => v != null && v !== "").map(([k, v]) => (
                          <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 font-mono">
                            {k}: {String(v)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      ) : (
        <div className="py-16 text-center text-stone-400 text-sm">Impossibile caricare lo stato di sistema.</div>
      )}
    </div>
  );
}
